import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Loader2, Save, Dumbbell, Flame, Activity, Lock, Utensils, Info, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SEX_OPTIONS = ['male', 'female'];
const GOAL_OPTIONS = ['fat_loss', 'definition', 'muscle_gain', 'performance', 'maintenance'];
const ACTIVITY_OPTIONS = ['sedentary', 'light', 'moderate', 'active', 'very_active'];
const EXPERIENCE_OPTIONS = ['beginner', 'intermediate', 'advanced'];
const SPORT_OPTIONS = ['walking', 'running', 'swimming', 'strength_home', 'strength_gym', 'cycling'];
const PARQ_KEYS = ['q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7'];

// Must match EQUIPMENT_OPTIONS in the backend (the library's real equipment values)
const EQUIPMENT_OPTIONS = ['body weight', 'dumbbell', 'barbell', 'pull_up_bar', 'cable', 'leverage machine', 'rope'];
const EQUIPMENT_LABELS = {
  es: {
    'body weight': 'Peso corporal', dumbbell: 'Mancuernas', barbell: 'Barra de pesas',
    pull_up_bar: 'Barra de dominadas', cable: 'Poleas', 'leverage machine': 'Máquinas', rope: 'Cuerda',
  },
  en: {
    'body weight': 'Bodyweight', dumbbell: 'Dumbbells', barbell: 'Barbell',
    pull_up_bar: 'Pull-up bar', cable: 'Cables', 'leverage machine': 'Machines', rope: 'Rope',
  },
};

// Plain-language explanation of every option, so you know which one to pick
const HELP_CONTENT = {
  es: {
    goal: {
      title: 'Objetivo',
      items: [
        ['Perder grasa', 'Déficit calórico marcado (~20% menos). Bajas peso a buen ritmo, asumiendo que puedes perder algo de músculo por el camino.'],
        ['Definición', 'Perder grasa PROTEGIENDO el músculo: déficit más suave (~12%), proteína más alta (2,4 g/kg) y fuerza pesada. Bajas más despacio pero conservas lo que has ganado. Ideal si ya tienes base y quieres "marcar".'],
        ['Ganar músculo', 'Ligero superávit (~10% más) + fuerza. Ganas músculo y, casi siempre, algo de grasa.'],
        ['Rendimiento', 'Para mejorar EN TU DEPORTE (más fuerte, más rápido, más resistencia), no por estética. Calorías en mantenimiento o ligero superávit para poder entrenar duro y recuperar.'],
        ['Mantenimiento', 'Mantener tu peso y composición actuales. Ideal si ya estás donde quieres.'],
      ],
    },
    activity: {
      title: 'Nivel de actividad',
      items: [
        ['Sedentario', 'Trabajo sentado y poco o ningún ejercicio.'],
        ['Ligero', 'Ejercicio suave 1-3 días por semana.'],
        ['Moderado', 'Ejercicio 3-5 días por semana.'],
        ['Activo', 'Ejercicio 6-7 días por semana.'],
        ['Muy activo', 'Entrenamiento muy exigente a diario o trabajo físico duro.'],
      ],
      note: 'Cuenta tu actividad TOTAL (trabajo + entreno). Es lo que multiplica tu gasto calórico: si te pasas, comerás de más.',
    },
    experience: {
      title: 'Nivel de experiencia',
      items: [
        ['Principiante', 'Menos de ~6 meses entrenando fuerza con regularidad, o vuelves tras mucho tiempo parado.'],
        ['Intermedio', 'Entre ~6 meses y 2 años entrenando de forma constante. Dominas la técnica de los básicos.'],
        ['Avanzado', 'Más de ~2 años entrenando de forma estructurada y progresando con constancia.'],
      ],
      note: 'Ante la duda, elige el nivel MÁS BAJO: progresarás igual y con mucho menos riesgo de lesión.',
    },
  },
  en: {
    goal: {
      title: 'Goal',
      items: [
        ['Fat loss', 'Clear calorie deficit (~20% less). You lose weight at a good pace, accepting you may lose some muscle along the way.'],
        ['Definition', 'Lose fat while PROTECTING muscle: softer deficit (~12%), higher protein (2.4 g/kg) and heavy strength work. You go down slower but keep what you built. Ideal if you already have a base and want to get lean.'],
        ['Muscle gain', 'Slight surplus (~10% more) + strength work. You gain muscle and, usually, some fat.'],
        ['Performance', 'To get better AT YOUR SPORT (stronger, faster, fitter), not for looks. Calories at maintenance or a slight surplus so you can train hard and recover.'],
        ['Maintenance', 'Keep your current weight and composition. Ideal if you are already where you want to be.'],
      ],
    },
    activity: {
      title: 'Activity level',
      items: [
        ['Sedentary', 'Desk job and little or no exercise.'],
        ['Light', 'Light exercise 1-3 days a week.'],
        ['Moderate', 'Exercise 3-5 days a week.'],
        ['Active', 'Exercise 6-7 days a week.'],
        ['Very active', 'Very demanding daily training or hard physical job.'],
      ],
      note: 'Count your TOTAL activity (work + training). It multiplies your calorie expenditure: overestimate it and you will eat too much.',
    },
    experience: {
      title: 'Experience level',
      items: [
        ['Beginner', 'Less than ~6 months training strength regularly, or coming back after a long break.'],
        ['Intermediate', 'Between ~6 months and 2 years training consistently. You have the basics down.'],
        ['Advanced', 'More than ~2 years training in a structured way and progressing consistently.'],
      ],
      note: 'When in doubt, pick the LOWER level: you will progress just the same with far less injury risk.',
    },
  },
};

// Every field label in this form must render identically, otherwise fields sitting side by
// side in the grid start at different heights. The shadcn Label is `inline` by default (a
// 19px inline box), while a flex one is exactly its 14px line: mixing them pushed the
// birth-date field 10px below the sex field.
const LABEL_CLS = 'font-manrope font-semibold text-charcoal flex items-center gap-1.5';

// Age from a YYYY-MM-DD string (mirrors the backend so the UI shows the same number)
function ageFromBirthDate(bd) {
  if (!bd) return null;
  const d = new Date(bd);
  if (isNaN(d.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age -= 1;
  return age >= 0 && age < 130 ? age : null;
}

const emptyForm = {
  sex: '', birth_date: '', age: '', height_cm: '', weight_kg: '',
  goal: '', activity_level: '', experience_level: '',
  parq: {}, injuries: '',
  sports: [], equipment: [], days_per_week: '', session_minutes: '', availability_notes: '',
};

export default function TrainingPage() {
  const { t, language } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [targets, setTargets] = useState(null);
  const [helpOpen, setHelpOpen] = useState(null); // 'goal' | 'activity' | 'experience'

  const isPremium = user?.plan === 'premium';

  useEffect(() => {
    if (!isPremium) { setLoading(false); return; }
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPremium]);

  const fetchProfile = async () => {
    try {
      const res = await fetch(`${API}/training/profile`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setTargets(data.targets || null);
        if (data.profile) {
          const p = data.profile;
          setForm({
            sex: p.sex || '',
            birth_date: p.birth_date ? String(p.birth_date).slice(0, 10) : '',
            age: p.age ?? '',
            height_cm: p.height_cm ?? '',
            weight_kg: p.weight_kg ?? '',
            goal: p.goal || '',
            activity_level: p.activity_level || '',
            experience_level: p.experience_level || '',
            parq: p.parq || {},
            injuries: p.injuries || '',
            sports: p.sports || [],
            // Old profiles stored free text; keep only values that match the current options
            equipment: (p.equipment || []).filter(e => EQUIPMENT_OPTIONS.includes(e)),
            days_per_week: p.days_per_week ?? '',
            session_minutes: p.session_minutes ?? '',
            availability_notes: p.availability_notes || '',
          });
        }
      }
    } catch (e) {
      console.error('Error fetching training profile:', e);
    } finally {
      setLoading(false);
    }
  };

  const toggleSport = (s) => {
    setForm(f => ({ ...f, sports: f.sports.includes(s) ? f.sports.filter(x => x !== s) : [...f.sports, s] }));
  };

  const helpData = (HELP_CONTENT[language] || HELP_CONTENT.es)[helpOpen] || null;
  const computedAge = ageFromBirthDate(form.birth_date) ?? (form.age || null);

  const toggleEquipment = (e) => {
    setForm(f => ({ ...f, equipment: f.equipment.includes(e) ? f.equipment.filter(x => x !== e) : [...f.equipment, e] }));
  };

  const toggleParq = (q) => {
    setForm(f => ({ ...f, parq: { ...f.parq, [q]: !f.parq[q] } }));
  };

  const handleSave = async () => {
    if (!form.sex || !computedAge || !form.height_cm || !form.weight_kg || !form.goal || !form.activity_level) {
      toast.error(t('training.errors.required'));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        sex: form.sex,
        birth_date: form.birth_date || null,
        age: computedAge ? parseInt(computedAge) : null,
        height_cm: parseFloat(form.height_cm),
        weight_kg: parseFloat(form.weight_kg),
        goal: form.goal,
        activity_level: form.activity_level,
        experience_level: form.experience_level || null,
        parq: form.parq,
        injuries: form.injuries || null,
        sports: form.sports,
        equipment: form.equipment,
        days_per_week: form.days_per_week ? parseInt(form.days_per_week) : null,
        session_minutes: form.session_minutes ? parseInt(form.session_minutes) : null,
        availability_notes: form.availability_notes || null,
      };
      const res = await fetch(`${API}/training/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const data = await res.json();
        setTargets(data.targets || null);
        toast.success(t('training.saved'));
      } else {
        const err = await res.json();
        toast.error(err.detail || t('errors.generic'));
      }
    } catch {
      toast.error(t('errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-12 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!isPremium) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-12 text-center">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-primary" />
          </div>
          <h1 className="font-manrope font-bold text-2xl text-charcoal mb-2">{t('training.title')}</h1>
          <p className="text-stone font-inter mb-6">{t('training.premiumOnly')}</p>
          <Button onClick={() => navigate('/pricing')} className="rounded-full bg-primary">
            {t('training.upgrade')}
          </Button>
        </div>
      </Layout>
    );
  }

  const SelectField = ({ label, value, onChange, options, prefix, helpKey }) => (
    <div className="space-y-1">
      <Label className={LABEL_CLS}>
        {label}
        {helpKey && (
          <button type="button" onClick={() => setHelpOpen(helpKey)} title={language === 'es' ? '¿Qué elijo?' : 'Which one?'} className="text-stone hover:text-primary">
            <Info className="w-4 h-4" />
          </button>
        )}
      </Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-12 rounded-xl border-border bg-white/50">
          <SelectValue placeholder={t('training.select')} />
        </SelectTrigger>
        <SelectContent>
          {options.map(o => (
            <SelectItem key={o} value={o}>{t(`training.${prefix}.${o}`)}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-2">
          <Dumbbell className="w-7 h-7 text-primary" />
          <h1 className="font-manrope font-bold text-3xl text-charcoal">{t('training.title')}</h1>
        </div>
        <p className="text-stone font-inter mb-4">{t('training.subtitle')}</p>

        <div className="flex flex-wrap gap-2 mb-8">
          <Button onClick={() => navigate('/training/plans')} className="rounded-full bg-primary">
            <Dumbbell className="w-4 h-4 mr-2" />
            {t('training.myPlans')}
          </Button>
          <Button variant="outline" onClick={() => navigate('/nutrition/plans')} className="rounded-full border-primary text-primary hover:bg-primary/5">
            <Utensils className="w-4 h-4 mr-2" />
            {t('training.nutritionPlans')}
          </Button>
          <Button variant="outline" onClick={() => navigate('/training/evolution')} className="rounded-full border-primary text-primary hover:bg-primary/5">
            <TrendingUp className="w-4 h-4 mr-2" />
            {t('training.progress')}
          </Button>
          <Button variant="outline" onClick={() => navigate('/exercises')} className="rounded-full border-primary text-primary hover:bg-primary/5">
            <Dumbbell className="w-4 h-4 mr-2" />
            {t('training.exerciseLibrary')}
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Physical data */}
            <Card className="bg-white rounded-3xl shadow-soft p-6 space-y-4">
              <h2 className="font-manrope font-bold text-lg text-charcoal">{t('training.sections.physical')}</h2>
              <div className="grid grid-cols-2 gap-4">
                <SelectField label={t('training.fields.sex')} value={form.sex} onChange={(v) => setForm({ ...form, sex: v })} options={SEX_OPTIONS} prefix="sex" />
                <div className="space-y-1">
                  <Label className={`${LABEL_CLS} whitespace-nowrap`}>
                    {language === 'es' ? 'Nacimiento' : 'Birth date'}
                  </Label>
                  <Input type="date" max={new Date().toISOString().slice(0, 10)} value={form.birth_date}
                    onChange={(e) => setForm({ ...form, birth_date: e.target.value })}
                    title={language === 'es' ? 'La edad se calcula sola cada año a partir de esta fecha' : 'Age updates itself every year from this date'}
                    className="h-12 rounded-xl border-border bg-white/50" />
                  <p className="text-xs text-stone font-inter h-4">
                    {computedAge != null ? `${computedAge} ${language === 'es' ? 'años' : 'years'}` : ''}
                  </p>
                </div>
                <div className="space-y-1">
                  <Label className={LABEL_CLS}>{t('training.fields.height')}</Label>
                  <Input type="number" min="100" max="250" value={form.height_cm} onChange={(e) => setForm({ ...form, height_cm: e.target.value })} className="h-12 rounded-xl border-border bg-white/50" />
                </div>
                <div className="space-y-1">
                  <Label className={LABEL_CLS}>{t('training.fields.weight')}</Label>
                  <Input type="number" min="30" max="300" step="0.1" value={form.weight_kg} onChange={(e) => setForm({ ...form, weight_kg: e.target.value })} className="h-12 rounded-xl border-border bg-white/50" />
                </div>
              </div>
            </Card>

            {/* Goal & activity */}
            <Card className="bg-white rounded-3xl shadow-soft p-6 space-y-4">
              <h2 className="font-manrope font-bold text-lg text-charcoal">{t('training.sections.goal')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <SelectField label={t('training.fields.goal')} value={form.goal} onChange={(v) => setForm({ ...form, goal: v })} options={GOAL_OPTIONS} prefix="goal" helpKey="goal" />
                <SelectField label={t('training.fields.activity')} value={form.activity_level} onChange={(v) => setForm({ ...form, activity_level: v })} options={ACTIVITY_OPTIONS} prefix="activity" helpKey="activity" />
                <SelectField label={t('training.fields.experience')} value={form.experience_level} onChange={(v) => setForm({ ...form, experience_level: v })} options={EXPERIENCE_OPTIONS} prefix="experience" helpKey="experience" />
              </div>
            </Card>

            {/* Health */}
            <Card className="bg-white rounded-3xl shadow-soft p-6 space-y-4">
              <h2 className="font-manrope font-bold text-lg text-charcoal">{t('training.sections.health')}</h2>
              <p className="text-sm text-stone font-inter">{t('training.parq.intro')}</p>
              <div className="space-y-2">
                {PARQ_KEYS.map(q => (
                  <label key={q} className="flex items-start gap-2 cursor-pointer">
                    <input type="checkbox" checked={!!form.parq[q]} onChange={() => toggleParq(q)} className="w-4 h-4 mt-1 accent-primary shrink-0" />
                    <span className="font-inter text-sm text-charcoal">{t(`training.parq.${q}`)}</span>
                  </label>
                ))}
              </div>
              <div className="space-y-1">
                <Label className="font-manrope font-semibold text-charcoal">{t('training.fields.injuries')}</Label>
                <Textarea value={form.injuries} onChange={(e) => setForm({ ...form, injuries: e.target.value })} placeholder={t('training.fields.injuriesPlaceholder')} className="rounded-xl border-border bg-white/50" />
              </div>
            </Card>

            {/* Sport & availability */}
            <Card className="bg-white rounded-3xl shadow-soft p-6 space-y-4">
              <h2 className="font-manrope font-bold text-lg text-charcoal">{t('training.sections.sport')}</h2>
              <div className="space-y-2">
                <Label className="font-manrope font-semibold text-charcoal">{t('training.fields.sports')}</Label>
                <div className="flex flex-wrap gap-2">
                  {SPORT_OPTIONS.map(s => (
                    <button key={s} type="button" onClick={() => toggleSport(s)}
                      className={`px-4 py-2 rounded-full font-manrope text-sm border-2 transition-all ${form.sports.includes(s) ? 'bg-primary text-white border-primary' : 'bg-white text-stone border-border hover:border-primary/50'}`}>
                      {t(`training.sport.${s}`)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="font-manrope font-semibold text-charcoal">{t('training.fields.daysPerWeek')}</Label>
                  <Input type="number" min="1" max="7" value={form.days_per_week} onChange={(e) => setForm({ ...form, days_per_week: e.target.value })} className="h-12 rounded-xl border-border bg-white/50" />
                </div>
                <div className="space-y-1">
                  <Label className="font-manrope font-semibold text-charcoal">{t('training.fields.sessionMinutes')}</Label>
                  <Input type="number" min="10" max="240" value={form.session_minutes} onChange={(e) => setForm({ ...form, session_minutes: e.target.value })} className="h-12 rounded-xl border-border bg-white/50" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="font-manrope font-semibold text-charcoal">{t('training.fields.equipment')}</Label>
                <p className="text-xs text-stone font-inter">
                  {language === 'es'
                    ? 'Marca solo lo que tengas de verdad: solo se te propondrán ejercicios que puedas hacer con ese material. Si no marcas nada, se usa toda la biblioteca.'
                    : 'Tick only what you really have: you will only get exercises you can do with it. If you tick nothing, the whole library is used.'}
                </p>
                <div className="flex flex-wrap gap-2">
                  {EQUIPMENT_OPTIONS.map(eq => (
                    <button key={eq} type="button" onClick={() => toggleEquipment(eq)}
                      className={`px-4 py-2 rounded-full font-manrope text-sm border-2 transition-all ${form.equipment.includes(eq) ? 'bg-primary text-white border-primary' : 'bg-white text-stone border-border hover:border-primary/50'}`}>
                      {(EQUIPMENT_LABELS[language] || EQUIPMENT_LABELS.es)[eq]}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1">
                <Label className="font-manrope font-semibold text-charcoal">{t('training.fields.availability')}</Label>
                <Textarea value={form.availability_notes} onChange={(e) => setForm({ ...form, availability_notes: e.target.value })} placeholder={t('training.fields.availabilityPlaceholder')} className="rounded-xl border-border bg-white/50" />
              </div>
            </Card>

            <Button onClick={handleSave} disabled={saving} className="w-full rounded-full bg-primary h-12 font-manrope font-semibold" data-testid="save-training-profile">
              {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Save className="w-5 h-5 mr-2" />}
              {t('training.save')}
            </Button>
          </div>

          {/* Targets card */}
          <div className="lg:col-span-1">
            <Card className="bg-white rounded-3xl shadow-soft p-6 space-y-4 sticky top-20">
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-sage" />
                <h2 className="font-manrope font-bold text-lg text-charcoal">{t('training.targets.title')}</h2>
              </div>
              {targets ? (
                <>
                  <div className="flex gap-3">
                    <div className="flex-1 bg-muted/50 rounded-2xl p-3 text-center">
                      <p className="text-xs text-stone">{t('training.targets.bmr')}</p>
                      <p className="font-manrope font-bold text-charcoal">{targets.bmr}</p>
                    </div>
                    <div className="flex-1 bg-muted/50 rounded-2xl p-3 text-center">
                      <p className="text-xs text-stone">{t('training.targets.tdee')}</p>
                      <p className="font-manrope font-bold text-charcoal">{targets.tdee}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {[['training_day', t('training.targets.trainingDay'), 'text-primary'], ['rest_day', t('training.targets.restDay'), 'text-sage']].map(([key, label, color]) => (
                      <div key={key} className="border border-border rounded-2xl p-3 space-y-1">
                        <p className={`font-manrope font-semibold text-sm ${color}`}>{label}</p>
                        <p className="flex items-center gap-1 font-manrope font-bold text-charcoal">
                          <Flame className="w-4 h-4 text-sage" /> {targets[key].calories} kcal
                        </p>
                        <p className="text-xs text-stone">{t('recipes.nutrition.protein')}: <b>{targets[key].protein_g} g</b></p>
                        <p className="text-xs text-stone">{t('recipes.nutrition.carbs')}: <b>{targets[key].carbs_g} g</b></p>
                        <p className="text-xs text-stone">{t('recipes.nutrition.fat')}: <b>{targets[key].fat_g} g</b></p>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-stone/70 italic leading-snug">{t('training.targets.disclaimer')}</p>
                </>
              ) : (
                <p className="text-sm text-stone font-inter">{t('training.targets.empty')}</p>
              )}
            </Card>
          </div>
        </div>
      </div>

      {/* Help modal explaining each option */}
      <Dialog open={!!helpOpen} onOpenChange={(o) => { if (!o) setHelpOpen(null); }}>
        <DialogContent className="rounded-3xl max-w-lg max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-manrope">{helpData?.title}</DialogTitle>
            <DialogDescription className="sr-only">{helpData?.title}</DialogDescription>
          </DialogHeader>
          {helpData && (
            <div className="space-y-3">
              {helpData.items.map(([name, desc], i) => (
                <div key={i}>
                  <p className="font-manrope font-semibold text-charcoal text-sm">{name}</p>
                  <p className="text-sm text-stone font-inter">{desc}</p>
                </div>
              ))}
              {helpData.note && (
                <p className="text-xs text-primary font-inter bg-primary/5 rounded-xl p-3 mt-2">{helpData.note}</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
